import { useEffect, useMemo, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { PaytrieAPI } from '@paytrie/client'

import { TransactionType, useBuySellStore } from '@/reducers/transactionReducer'
import { useLimits } from '@/hooks/useLimits'
import { track } from '@/utils/analytics'
import { useDebounce } from './useDebounce'

const QUOTE_DEBOUNCE_MS = 400
const QUOTE_REFETCH_MS = 60_000

export type QuoteAnchorSide = 'left' | 'right'

// Fetches a v2 quote whenever the buy/sell inputs change. The inputs are
// debounced so a burst of keystrokes collapses into a single request. The
// anchored side (the input the user edited) drives leftSideValue vs
// rightSideValue; the opposite side is the calculated result.
export const useQuoteV2 = (anchorSide: QuoteAnchorSide) => {
  const transactionType = useBuySellStore.use.transactionType()
  const network = useBuySellStore.use.network()
  const stableCoin = useBuySellStore.use.stableCoin()
  const fiatAmount = useBuySellStore.use.fiatAmount()
  const stableCoinAmount = useBuySellStore.use.stableCoinAmount()
  const setQuotes = useBuySellStore.use.setQuotesV2()
  const setFiatAmount = useBuySellStore.use.setFiatAmountV2()
  const setStableCoinAmount = useBuySellStore.use.setStableCoinAmountV2()

  const isBuy = transactionType === TransactionType.BUY
  const networkSymbol = `${stableCoin}-${network}`
  const leftSideLabel = (
    isBuy ? 'CAD' : networkSymbol
  ) as PaytrieAPI.GetQuotesLeftSideLabel
  const rightSideLabel = (
    isBuy ? networkSymbol : 'CAD'
  ) as PaytrieAPI.GetQuotesRightSideLabel

  const payAmount = isBuy ? fiatAmount : stableCoinAmount
  const receiveAmount = isBuy ? stableCoinAmount : fiatAmount
  const anchorValue = anchorSide === 'left' ? payAmount : receiveAmount
  const anchorCurrency = isBuy === (anchorSide === 'left') ? 'CAD' : stableCoin
  const minimum = useLimits()?.leftSideCurrency[anchorCurrency].minimum

  const quoteInput = useMemo(
    () => ({
      leftSideLabel,
      rightSideLabel,
      anchorSide,
      anchorValue,
    }),
    [leftSideLabel, rightSideLabel, anchorSide, anchorValue]
  )
  const debouncedInput = useDebounce(quoteInput, QUOTE_DEBOUNCE_MS)

  const { data, isError } = useQuery({
    queryKey: ['v2', 'quote', debouncedInput],
    queryFn: async () => {
      const params: PaytrieAPI.GetQuotesParams = {
        leftSideLabel: debouncedInput.leftSideLabel,
        rightSideLabel: debouncedInput.rightSideLabel,
        ...(debouncedInput.anchorSide === 'left'
          ? { leftSideValue: debouncedInput.anchorValue }
          : { rightSideValue: debouncedInput.anchorValue }),
      }
      const res = await PaytrieAPI.getQuotes(params)
      if (res.status !== 200 || !res.data.data) {
        throw new Error('Quote request failed')
      }
      return res.data.data
    },
    enabled: minimum !== undefined && debouncedInput.anchorValue >= minimum,
    refetchInterval: QUOTE_REFETCH_MS,
    retry: 1,
  })

  useEffect(() => {
    if (data) {
      setQuotes(data)
    }
  }, [data, setQuotes])

  const wasError = useRef(false)
  useEffect(() => {
    if (isError && !wasError.current) {
      setQuotes(null)
      track('quote_failed')
    }
    wasError.current = isError
  }, [isError, setQuotes])

  useEffect(() => {
    const { anchorValue: value, anchorSide: side } = debouncedInput
    if (minimum === undefined || value < 0 || value >= minimum) {
      return
    }
    // Below the minimum (including cleared inputs): zero the opposite
    // (calculated) side, clear the quote.
    const setPayAmount = isBuy ? setFiatAmount : setStableCoinAmount
    const setReceiveAmount = isBuy ? setStableCoinAmount : setFiatAmount
    if (side === 'left') {
      setReceiveAmount(0)
    } else {
      setPayAmount(0)
    }
    setQuotes(null)
  }, [
    debouncedInput,
    minimum,
    isBuy,
    setFiatAmount,
    setStableCoinAmount,
    setQuotes,
  ])
}
